// Channel-aware production stages (handoff 5.1). One source of truth for
// the production board, the This Week generator, and the content tools.
//
// Stage ids double as marketing_time_log activity_type values when the
// board's time-on-move prompt logs hours against a move.

export const CHANNELS = [
  { id: 'blog', label: 'Blog' },
  { id: 'youtube', label: 'YouTube' },
];

export const STAGE_LABELS = {
  idea: 'Idea',
  planned: 'Planned',
  drafting: 'Drafting',
  scripting: 'Scripting',
  recording: 'Recording',
  editing: 'Editing',
  graphics: 'Graphics',
  scheduled: 'Scheduled',
  published: 'Published',
};

const BLOG_STAGES = ['idea', 'planned', 'drafting', 'editing', 'graphics', 'scheduled', 'published'];
const YOUTUBE_STAGES = ['idea', 'planned', 'scripting', 'recording', 'editing', 'graphics', 'scheduled', 'published'];

export function stagesForChannel(channel) {
  return channel === 'youtube' ? YOUTUBE_STAGES : BLOG_STAGES;
}

// Stages that appear as board columns: in-flight work only (handoff 8) —
// ideas live in the idea bank, published pieces in the performance view.
export function boardStagesForChannel(channel) {
  return stagesForChannel(channel).filter(s => s !== 'idea' && s !== 'published');
}

export function nextStage(status, channel) {
  const stages = stagesForChannel(channel);
  const i = stages.indexOf(status);
  return i >= 0 && i < stages.length - 1 ? stages[i + 1] : null;
}

// The verb This Week shows for an item sitting in a stage (the work that
// moves it forward), plus the weekday it defaults to (0=Mon..4=Fri):
// heavier production early in the week, publishing later.
export const STAGE_ACTION = {
  planned: { verb: 'Start', dayIdx: 0 },
  drafting: { verb: 'Draft', dayIdx: 0 },
  scripting: { verb: 'Script', dayIdx: 0 },
  recording: { verb: 'Record', dayIdx: 1 },
  editing: { verb: 'Edit', dayIdx: 1 },
  graphics: { verb: 'Graphics', dayIdx: 2 },
  scheduled: { verb: 'Publish', dayIdx: 2 },
};

// Queue depth = anything in flight past idea, not yet published.
const IN_FLIGHT = new Set(['planned', 'drafting', 'scripting', 'recording', 'editing', 'graphics', 'scheduled']);
export function isInFlight(status) {
  return IN_FLIGHT.has(status);
}

export const PRIORITIES = [
  { id: 'high', label: 'High' },
  { id: 'med', label: 'Med' },
  { id: 'low', label: 'Low' },
];

// ── quarter helpers ──
export function quarterOf(d = new Date()) {
  return `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
}

export function nextQuarterOf(d = new Date()) {
  const q = Math.floor(d.getMonth() / 3) + 1;
  return q === 4 ? `${d.getFullYear() + 1}-Q1` : `${d.getFullYear()}-Q${q + 1}`;
}

// Mondays of a quarter ('2026-Q3' -> [Date...]), for plan_week slots.
export function mondaysOfQuarter(quarter) {
  const m = /^(\d{4})-Q([1-4])$/.exec(quarter || '');
  if (!m) return [];
  const year = Number(m[1]);
  const startMonth = (Number(m[2]) - 1) * 3;
  const start = new Date(year, startMonth, 1);
  const end = new Date(year, startMonth + 3, 0);
  const d = new Date(start);
  const offset = (8 - d.getDay()) % 7; // first Monday on/after the 1st
  d.setDate(d.getDate() + offset);
  const mondays = [];
  while (d <= end) {
    mondays.push(new Date(d));
    d.setDate(d.getDate() + 7);
  }
  return mondays;
}

// Local YYYY-MM-DD (toISOString would shift the date across midnight UTC).
export function toDateKey(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
